import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./AuthContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";

import AdminDashboard from "./pages/admin/AdminDashboard";
import ClientsPage from "./pages/admin/ClientsPage";
import AdminClientDetailPage from "./pages/admin/AdminClientDetailPage";
import AmbassadorsPage from "./pages/admin/AmbassadorsPage";
import AdminAmbassadorDetailPage from "./pages/admin/AdminAmbassadorDetailPage";
import ProjectsPage from "./pages/admin/ProjectsPage";
import AdminProjectDetailPage from "./pages/admin/AdminProjectDetailPage";
import ApplicationsPage from "./pages/admin/ApplicationsPage";
import SubmissionsPage from "./pages/admin/SubmissionsPage";
import PayoutsPage from "./pages/admin/PayoutsPage";
import ReportsPage from "./pages/admin/ReportsPage";
import SettingsPage from "./pages/admin/SettingsPage";

import ClientDashboard from "./pages/client/ClientDashboard";
import ClientProjectsPage from "./pages/client/ClientProjectsPage";
import ClientProjectDetailPage from "./pages/client/ClientProjectDetailPage";
import ClientTaskDetailPage from "./pages/client/ClientTaskDetailPage";
import ClientAmbassadorsPage from "./pages/client/ClientAmbassadorsPage";
import ClientAmbassadorDetailPage from "./pages/client/ClientAmbassadorDetailPage";
import ClientSubmissionsPage from "./pages/client/ClientSubmissionsPage";
import ClientReportsPage from "./pages/client/ClientReportsPage";
import ClientIssuesPage from "./pages/client/ClientIssuesPage";

import AmbassadorDashboard from "./pages/ambassador/AmbassadorDashboard";
import AmbassadorProjectsPage from "./pages/ambassador/AmbassadorProjectsPage";
import AmbassadorProjectDetailPage from "./pages/ambassador/AmbassadorProjectDetailPage";
import AmbassadorTaskDetailPage from "./pages/ambassador/AmbassadorTaskDetailPage";
import AmbassadorProfilePage from "./pages/ambassador/AmbassadorProfilePage";
import AmbassadorAnnouncementsPage from "./pages/ambassador/AmbassadorAnnouncementsPage";
import ClientCommunicatePage from "./pages/client/ClientCommunicatePage";
import AdminVerificationsPage from "./pages/admin/AdminVerificationsPage";
import AmbassadorPayoutsPage from "./pages/ambassador/AmbassadorPayoutsPage";
import LeaderboardPage from "./pages/ambassador/LeaderboardPage";
import LandingPage from "./pages/LandingPage";
import LoginSelector from "./pages/LoginSelector";
import GetStarted from "./pages/GetStarted";
import AdminOrganizationsPage from "./pages/admin/AdminOrganizationsPage";
import AdminLeadsPage from "./pages/admin/AdminLeadsPage";
import ClientBrandingPage from "./pages/client/ClientBrandingPage";

function Protected({ allow }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="app-bg min-h-screen grid place-items-center text-muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allow && !allow.includes(user.role)) {
    const dest = user.role === "admin" ? "/admin" : user.role === "client" ? "/client" : "/me";
    return <Navigate to={dest} replace />;
  }
  return <Layout><Outlet /></Layout>;
}

/** Landing on `/` for logged-out; redirect logged-in users to their workspace.
 *  Renders the (data-free) landing page immediately instead of blocking on the
 *  auth check — `user` is falsy both while loading and once confirmed
 *  logged-out, so this never shows a blank screen. If the check later confirms
 *  a session, the redirect below fires on the next render. */
function RootGate() {
  const { user } = useAuth();
  if (user) {
    const dest = user.role === "admin" ? "/admin" : user.role === "client" ? "/client" : "/me";
    return <Navigate to={dest} replace />;
  }
  return <LandingPage />;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="app-bg min-h-screen grid place-items-center text-muted">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "client") return <Navigate to="/client" replace />;
  return <Navigate to="/me" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster theme="dark" position="top-right" toastOptions={{
        style: { background: "rgba(17,24,39,0.95)", border: "1px solid rgba(255,255,255,0.1)", color: "#F9FAFB" }
      }} />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootGate />} />
          <Route path="/get-started" element={<GetStarted />} />
          <Route path="/login" element={<LoginSelector />} />
          <Route path="/login/brand" element={<Login variant="brand" />} />
          <Route path="/login/agency" element={<Login variant="agency" />} />
          <Route path="/login/creator" element={<Login variant="creator" />} />
          <Route path="/admin-login" element={<Login adminMode />} />

          {/* Phase-2 path-based tenant routing: /o/<slug>/... — branding auto-applies via useBranding */}
          <Route path="/o/:slug/login" element={<LoginSelector />} />
          <Route path="/o/:slug/login/brand" element={<Login variant="brand" />} />
          <Route path="/o/:slug/login/agency" element={<Login variant="agency" />} />
          <Route path="/o/:slug/login/creator" element={<Login variant="creator" />} />
          <Route path="/o/:slug/get-started" element={<GetStarted />} />

          <Route element={<Protected allow={["admin"]} />}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/clients" element={<ClientsPage />} />
            <Route path="/admin/clients/:cid" element={<AdminClientDetailPage />} />
            <Route path="/admin/ambassadors" element={<AmbassadorsPage />} />
            <Route path="/admin/ambassadors/:aid" element={<AdminAmbassadorDetailPage />} />
            <Route path="/admin/projects" element={<ProjectsPage />} />
            <Route path="/admin/projects/:pid" element={<AdminProjectDetailPage />} />
            <Route path="/admin/applications" element={<ApplicationsPage />} />
            <Route path="/admin/submissions" element={<SubmissionsPage />} />
            <Route path="/admin/verifications" element={<AdminVerificationsPage />} />
            <Route path="/admin/organizations" element={<AdminOrganizationsPage />} />
            <Route path="/admin/leads" element={<AdminLeadsPage />} />
            <Route path="/admin/payouts" element={<PayoutsPage />} />
            <Route path="/admin/reports" element={<ReportsPage />} />
            <Route path="/admin/settings" element={<SettingsPage />} />
          </Route>

          <Route element={<Protected allow={["client"]} />}>
            <Route path="/client" element={<ClientDashboard />} />
            <Route path="/client/projects" element={<ClientProjectsPage />} />
            <Route path="/client/projects/:pid" element={<ClientProjectDetailPage />} />
            <Route path="/client/projects/:pid/tasks/:tid" element={<ClientTaskDetailPage />} />
            <Route path="/client/ambassadors" element={<ClientAmbassadorsPage />} />
            <Route path="/client/ambassadors/:aid" element={<ClientAmbassadorDetailPage />} />
            <Route path="/client/submissions" element={<ClientSubmissionsPage />} />
            <Route path="/client/communicate" element={<ClientCommunicatePage />} />
            <Route path="/client/reports" element={<ClientReportsPage />} />
            <Route path="/client/branding" element={<ClientBrandingPage />} />
            <Route path="/client/issues" element={<ClientIssuesPage />} />
          </Route>

          <Route element={<Protected allow={["ambassador"]} />}>
            <Route path="/me" element={<AmbassadorDashboard />} />
            <Route path="/me/projects" element={<AmbassadorProjectsPage />} />
            <Route path="/me/projects/:pid" element={<AmbassadorProjectDetailPage />} />
            <Route path="/me/projects/:pid/tasks/:tid" element={<AmbassadorTaskDetailPage />} />
            <Route path="/me/profile" element={<AmbassadorProfilePage />} />
            <Route path="/me/updates" element={<AmbassadorAnnouncementsPage />} />
            <Route path="/me/payouts" element={<AmbassadorPayoutsPage />} />
            <Route path="/me/leaderboard" element={<LeaderboardPage />} />
          </Route>

          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
